/**
 * Line Item De-duplication — pure domain logic for detecting and removing
 * duplicate line items that arise when a long till slip / multi-page A4
 * transaction is captured as several overlapping photos.
 *
 * When the user photographs a long receipt in overlapping segments (so that
 * no line items are missed), the AI extraction will frequently return the
 * overlapping items twice. These duplicates are typically near-adjacent and
 * identical in description, quantity and price. This module identifies them
 * so the UI can flag them for review and optionally remove them before
 * submission.
 *
 * Extracted from the capture flow to enable independent testing without React.
 */

import type { DocumentLineItem } from '@/hooks/useDocumentsWorkItems';

/** Tolerance (in currency units) when comparing prices/totals. */
const PRICE_TOLERANCE = 0.01;
/** Tolerance when comparing quantities. */
const QTY_TOLERANCE = 0.001;

/**
 * Normalize a line item description for comparison.
 * - lowercases
 * - trims
 * - collapses internal whitespace
 * - strips trailing quantity/price hints some OCR engines append
 *   (e.g. "Coke 500ml x2" → "coke 500ml x2" — we keep these since they are
 *   part of the product label, but we strip leading line numbers like "1. ")
 */
export function normalizeDescription(description: string): string {
  if (!description) return '';
  let s = String(description).toLowerCase().trim();
  // Strip a leading line number such as "1. ", "12) ", "001 - "
  s = s.replace(/^\d+[\s).\-]+/, '');
  // Collapse runs of whitespace
  s = s.replace(/\s+/g, ' ');
  return s.trim();
}

/**
 * A group of line item indices that are considered duplicates of one another.
 * The first occurrence is the "original" (kept); subsequent occurrences are
 * flagged as duplicates (candidates for removal).
 */
export interface DuplicateLineItemGroup {
  /** Signature used to match the items. */
  signature: string;
  /** Index of the first occurrence (the one we keep). */
  originalIndex: number;
  /** Indices of subsequent occurrences (candidates for removal). */
  duplicateIndices: number[];
}

export interface DetectDuplicateLineItemsResult {
  groups: DuplicateLineItemGroup[];
  /** Flat set of all indices flagged as duplicates (across all groups). */
  duplicateIndexSet: Set<number>;
  /** Total count of duplicate (removable) line items. */
  duplicateCount: number;
}

/**
 * Build a comparison signature for a line item. Two items with the same
 * signature are considered duplicates.
 *
 * Matches when normalized description AND (unit_price + quantity) OR
 * (line_total) are equal within tolerance. We include both signals because
 * OCR sometimes captures the unit price for one copy and only the line total
 * for the other.
 */
function buildSignature(item: DocumentLineItem): string {
  const desc = normalizeDescription(item.description);
  const qty = Number(item.quantity) || 0;
  const price = Number(item.unit_price) || 0;
  const lineTotal = item.line_total != null ? Number(item.line_total) : qty * price;

  // Round to 2 decimals so floating point noise doesn't break matching
  const priceKey = Math.round(price * 100) / 100;
  const totalKey = Math.round(lineTotal * 100) / 100;
  const qtyKey = Math.round(qty * 1000) / 1000;

  return `${desc}|q${qtyKey}|p${priceKey}|t${totalKey}`;
}

/**
 * Detect duplicate line items within a single receipt extraction.
 *
 * Returns groups of duplicates. The first occurrence is always treated as the
 * original; later occurrences are flagged as duplicates.
 *
 * @param items Line items extracted from the receipt (in order)
 * @param minGroupSize Only return groups with at least this many duplicates
 *   (default 1 — i.e. any group with at least one duplicate)
 */
export function detectDuplicateLineItems(
  items: DocumentLineItem[],
  minGroupSize = 1,
): DetectDuplicateLineItemsResult {
  const groups: DuplicateLineItemGroup[] = [];
  const duplicateIndexSet = new Set<number>();

  if (!items || items.length === 0) {
    return { groups, duplicateIndexSet, duplicateCount: 0 };
  }

  // Map signature → group being built
  const seen = new Map<string, DuplicateLineItemGroup>();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    // Skip blank/empty descriptions — they can't be meaningfully matched and
    // are usually OCR noise that the user will delete anyway.
    if (!normalizeDescription(item.description)) continue;

    const sig = buildSignature(item);
    const existing = seen.get(sig);
    if (existing) {
      existing.duplicateIndices.push(i);
      duplicateIndexSet.add(i);
    } else {
      const group: DuplicateLineItemGroup = {
        signature: sig,
        originalIndex: i,
        duplicateIndices: [],
      };
      seen.set(sig, group);
    }
  }

  let duplicateCount = 0;
  for (const group of seen.values()) {
    if (group.duplicateIndices.length >= minGroupSize) {
      groups.push(group);
      duplicateCount += group.duplicateIndices.length;
    }
  }

  return { groups, duplicateIndexSet, duplicateCount };
}

/**
 * Return a new array with the duplicate line items removed (first occurrence
 * of each signature is kept, subsequent occurrences are dropped).
 *
 * @param items Line items extracted from the receipt
 * @param duplicateIndexSet Set of indices to remove (from
 *   detectDuplicateLineItems). If omitted, detection is run inline.
 */
export function deduplicateLineItems(
  items: DocumentLineItem[],
  duplicateIndexSet?: Set<number>,
): DocumentLineItem[] {
  const toRemove = duplicateIndexSet ?? detectDuplicateLineItems(items).duplicateIndexSet;
  if (toRemove.size === 0) return items;
  return items.filter((_, idx) => !toRemove.has(idx));
}
